/**
 * Measure the visible glyph width (in mm) of a live, font-loaded text element.
 *
 * Imported (custom-*) fonts can't be re-measured by rebuilding a throwaway
 * FabricPathText — that object never loads the custom font, so calcTextWidth()
 * collapses to ~0. The live fabric element on the canvas HAS the font loaded,
 * so we read the visible extent straight off it. A small margin (matching
 * FabricPathText.getElementBoundingRect) avoids clipping the trailing glyph.
 *
 * Returns undefined when the measurement is not usable (NaN / non-positive),
 * so the caller can leave the existing width untouched.
 */
interface MeasurableTextElement {
  calcTextWidth(): number;
  _calcLineHeightWithoutSpacing(): number;
  text?: string;
}

export default function getCustomFontContentWidth(
  element: MeasurableTextElement,
  px2mm: (size: number) => number,
): number | undefined {
  const rawTextWidthPx = element.calcTextWidth();
  const marginPx = element._calcLineHeightWithoutSpacing() / 4;
  const contentWidthPx = rawTextWidthPx + marginPx;

  if (!Number.isFinite(contentWidthPx) || contentWidthPx <= 0) {
    return undefined;
  }

  // Collapse guard: when the custom font fails to load, calcTextWidth() returns
  // a near-zero value for text that actually has visible characters (the font
  // has no real glyph metrics), which would shrink the box to a sliver and wrap
  // the text vertically. Reject only that case — non-empty text measuring ~0px —
  // and let the caller keep the existing (path-visual) width instead. A genuine
  // narrow glyph ('.', 'i', 'l') still measures well above this floor, so it is
  // not falsely rejected.
  const textContent = element.text ?? '';
  if (textContent.trim().length > 0 && rawTextWidthPx < 1) {
    return undefined;
  }

  return px2mm(contentWidthPx);
}
